import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isPaidSubscription } from '@/lib/pricing';

interface Subscription {
  tier: 'free' | 'pro' | 'teams';
  status: 'active' | 'canceled' | 'past_due' | 'trialing';
  limits: {
    daily_voice_conversations: number;
    daily_text_chats: number;
    max_situations: number;
    vocab_per_situation: number;
    history_days: number;
    custom_situations: boolean;
    priority_support: boolean;
    monthly_api_minutes: number;
  };
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  loading: boolean;
  isPro: boolean;
  isTeams: boolean;
  isFree: boolean;
  isPaid: boolean;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const DEFAULT_UNPAID_SUBSCRIPTION: Subscription = {
  tier: 'free',
  status: 'active',
  limits: {
    daily_voice_conversations: 0,
    daily_text_chats: 0,
    max_situations: 0,
    vocab_per_situation: 10,
    history_days: 0,
    custom_situations: false,
    priority_support: false,
    monthly_api_minutes: 0,
  },
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
};

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setSubscription(DEFAULT_UNPAID_SUBSCRIPTION);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.rpc('get_user_subscription');
      
      if (!error && data?.[0]) {
        const row = data[0];
        setSubscription({
          tier: row.tier as 'free' | 'pro' | 'teams',
          status: row.status as 'active' | 'canceled' | 'past_due' | 'trialing',
          limits: {
            daily_voice_conversations: row.daily_voice_conversations,
            daily_text_chats: row.daily_text_chats,
            max_situations: row.max_situations,
            vocab_per_situation: row.vocab_per_situation,
            history_days: row.history_days,
            custom_situations: row.custom_situations,
            priority_support: row.priority_support,
            monthly_api_minutes: row.monthly_api_minutes ?? 0,
          },
          currentPeriodEnd: row.current_period_end,
          cancelAtPeriodEnd: row.cancel_at_period_end,
        });
      } else {
        setSubscription(DEFAULT_UNPAID_SUBSCRIPTION);
      }
    } catch (e) {
      console.error('Error fetching subscription:', e);
      setSubscription(DEFAULT_UNPAID_SUBSCRIPTION);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
    
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(() => {
      fetchSubscription();
    });

    return () => authSub.unsubscribe();
  }, []);

  const isPro = subscription?.tier === 'pro';
  const isTeams = subscription?.tier === 'teams';
  const isFree = subscription?.tier === 'free';
  const isPaid = isPaidSubscription(subscription);

  return (
    <SubscriptionContext.Provider value={{ 
      subscription, 
      loading, 
      isPro, 
      isTeams,
      isFree,
      isPaid,
      refresh: fetchSubscription 
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
